import React, { useState, useMemo, useEffect, useCallback } from 'react';
import './DataTable.css';
import '../assets/flags.css';
import { getAirlineLogo, hasAirlineLogo, getMilitaryLogo, hasMilitaryLogo, useAirlineLogoMapVersion } from '../utils/airlineLogoMap';
import { getAircraftSpritePosition, getAircraftSizeMultiplier } from '../utils/aircraftSpriteMap';
import { isMilitaryAircraft } from '../utils/icaoCountryMap';
import { resolveCountryCode } from '../utils/aircraftCountry';
import { getEmergencySquawk } from '../utils/emergencySquawk';
import { UNIT_SYSTEMS, convertDistance, convertAltitude, convertVerticalSpeed, getVerticalSpeedUnit } from '../utils/mapStyles';
import { formatEtaClock, arrivalDeltaMinutes } from '../utils/timezones';
import { useUnitsSettings, useColumnConfig } from '../contexts/SettingsContext';
import { filterAndOrderColumns } from '../utils/columnConfig';
import spriteImage from '../assets/BelugaProject/beluga_sprites.png';
import { isGroundVehicle } from '../utils/groundVehicle';
import { airlineCodeFromCallsign } from '../utils/callsignAirline';
import { GroundVehicleIcon } from './AircraftTableComponents';

// Mobile card component for individual aircraft
const AircraftCard = React.memo(function AircraftCard({
  aircraft,
  isSelected,
  isExpanded,
  onSelect,
  onToggleExpand,
  unitSystem
}) {
  const isNew = !aircraft.last_seen_at;
  const hasNoPosition = aircraft.lat == null || aircraft.lon == null;
  const isMilitary = aircraft.military === true || isMilitaryAircraft(aircraft.category);
  const emergency = getEmergencySquawk(aircraft);
  const units = UNIT_SYSTEMS[unitSystem] || UNIT_SYSTEMS.metric;

  // Get country flag
  const countryCode = resolveCountryCode(aircraft, { registration: 'r' });

  // Get aircraft sprite
  const aircraftType = aircraft.t;
  const spritePos = getAircraftSpritePosition(aircraftType);
  // Fixed in the list. The size multiplier exists so a 747 outranks a Cessna on
  // the MAP; in a list it only makes the row as tall as the biggest aircraft on
  // screen, which is what left widebody rows noticeably deeper than the rest.
  const iconSize = 22;

  // Calculate altitude with unit conversion
  const convertedAlt = aircraft.alt_baro ? Math.round(convertAltitude(aircraft.alt_baro, unitSystem)) : null;
  const baroRate = aircraft.baro_rate || 0;

  // Click only (as the airport board rows): a touchend handler also fired at
  // the end of every scroll that lifted off over a card, and its
  // preventDefault left the card stuck in :active on iOS.
  const handleTap = useCallback((e) => {
    e.stopPropagation();
    onToggleExpand(aircraft.hex);
  }, [aircraft.hex, onToggleExpand]);

  const handleSelect = useCallback((e) => {
    e.stopPropagation();
    onSelect(isSelected ? null : aircraft);
  }, [aircraft, isSelected, onSelect]);

  return (
    <div
      className={`aircraft-card ${isNew ? 'new' : ''} ${isSelected ? 'selected' : ''} ${hasNoPosition ? 'no-position' : ''} ${isMilitary ? 'military' : ''} ${emergency ? 'emergency' : ''} ${isExpanded ? 'expanded' : ''}`}
      onClick={handleTap}
    >
      <div className="card-main">
        {/* Flag */}
        <div className="card-flag">
          {countryCode ? (
            <span className={`flag flag-${countryCode.toLowerCase()}`}></span>
          ) : (
            <span className="flag-placeholder">-</span>
          )}
        </div>

        {/* Callsign/Registration */}
        <div className="card-callsign">
          <span className="primary">{aircraft.flight?.trim() || aircraft.hex}</span>
          {aircraft.r && <span className="secondary">{aircraft.r}</span>}
        </div>

        {/* Aircraft Type with Icon */}
        <div className="card-type">
          <span
            className="aircraft-sprite"
            style={{
              width: `${iconSize}px`,
              height: `${iconSize}px`,
              backgroundImage: `url(${spriteImage})`,
              backgroundSize: `${iconSize * 8}px auto`,
              backgroundPosition: `${-(spritePos.col * iconSize)}px ${-(spritePos.row * iconSize)}px`,
              transform: 'rotate(90deg)',
            }}
          ></span>
          <span className="type-code">{aircraftType || '?'}</span>
        </div>

        {/* Altitude */}
        <div className="card-altitude">
          {convertedAlt !== null ? (
            <>
              <span className="alt-value">{convertedAlt.toLocaleString()}</span>
              <span className="alt-unit">{units.altitude}</span>
              {baroRate !== 0 && (
                <span className={`alt-trend ${baroRate > 100 ? 'climbing' : baroRate < -100 ? 'descending' : ''}`}>
                  {baroRate > 100 ? '↑' : baroRate < -100 ? '↓' : '→'}
                </span>
              )}
            </>
          ) : (
            <span className="no-data">-</span>
          )}
        </div>

        {/* Status indicators */}
        <div className="card-status">
          {isNew && <span className="status-badge new-badge">NEW</span>}
          {isMilitary && <span className="status-badge mil-badge">MIL</span>}
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="card-expanded">
          <div className="expanded-row">
            <span className="label">Squawk:</span>
            <span className="value">{aircraft.squawk || '-'}</span>
          </div>
          <div className="expanded-row">
            <span className="label">Speed:</span>
            <span className="value">
              {aircraft.gs ? (
                unitSystem === 'aeronautical' ? `${Math.round(aircraft.gs)} kts` :
                unitSystem === 'imperial' ? `${Math.round(aircraft.gs * 1.15078)} mph` :
                `${Math.round(aircraft.gs * 1.852)} km/h`
              ) : '-'}
            </span>
          </div>
          <div className="expanded-row">
            <span className="label">Distance:</span>
            <span className="value">
              {aircraft.r_dst ? `${convertDistance(aircraft.r_dst, unitSystem).toFixed(1)} ${units.distance}` : '-'}
            </span>
          </div>
          <div className="expanded-row">
            <span className="label">V/S:</span>
            <span className="value">
              {baroRate !== 0 ? (
                unitSystem === 'metric'
                  ? `${(baroRate >= 0 ? '+' : '')}${convertVerticalSpeed(baroRate, unitSystem).toFixed(1)} ${getVerticalSpeedUnit(unitSystem)}`
                  : `${(baroRate >= 0 ? '+' : '')}${Math.round(convertVerticalSpeed(baroRate, unitSystem))} ${getVerticalSpeedUnit(unitSystem)}`
              ) : '0'}
            </span>
          </div>
          <div className="expanded-row">
            <span className="label">Position:</span>
            <span className="value">
              {aircraft.lat != null && aircraft.lon != null
                ? `${aircraft.lat.toFixed(4)}, ${aircraft.lon.toFixed(4)}`
                : 'Verifying...'}
            </span>
          </div>
          <div className="expanded-row">
            <span className="label">Messages:</span>
            <span className="value">{aircraft.messages || 0}</span>
          </div>
          <div className="expanded-row">
            <span className="label">RSSI:</span>
            <span className="value">{aircraft.rssi != null ? aircraft.rssi.toFixed(1) : '-'}</span>
          </div>
          <button
            className="card-select-btn"
            onClick={handleSelect}
          >
            {isSelected ? 'Deselect' : 'Show on Map'}
          </button>
        </div>
      )}
    </div>
  );
});

// Function to generate columns based on unit system
const getColumns = (unitSystem) => {
  const units = UNIT_SYSTEMS[unitSystem] || UNIT_SYSTEMS.metric;
  const altUnit = units.altitude;
  const distUnit = units.distance;

  return [
    {
      key: 'flag',
      label: 'Flag',
      render: (row) => {
        // Show country flag for all aircraft (civilian and military)
        const countryCode = resolveCountryCode(row, { registration: 'r' });
        if (!countryCode) return <span>-</span>;
        return <span className={`flag flag-${countryCode.toLowerCase()}`}></span>;
      }
    },
    { key: 'hex', label: 'Hex ID' },
    { key: 'r', label: 'Reg', className: 'reg-column' },
    {
      key: 'airline_logo',
      label: 'Operator',
      render: (row) => {
        if (isGroundVehicle(row)) {
          return '-'; // a DAA truck has no operator; its callsign is a fleet ID, not an airline
        }
        // Check if aircraft is military - prefer backend flag, fallback to category check
        const isMilitary = row.military === true || isMilitaryAircraft(row.category);

        if (isMilitary) {
          // For military aircraft, show operator name from backend (airline_name field)
          const operatorName = row.airline_name || row.operator;
          const countryCode = resolveCountryCode(row, { registration: 'r' });

          // Try to show military insignia if available, using operator name for service-specific icons
          if (countryCode && hasMilitaryLogo(countryCode)) {
            const militaryIconPath = getMilitaryLogo(countryCode, operatorName);
            return (
              <>
                <img
                  src={militaryIconPath}
                  alt={operatorName || 'Military'}
                  className="airline-logo"
                  title={operatorName || 'Military'}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'inline';
                    }
                  }}
                />
                <span className="airline-text" style={{display: 'none'}} title={operatorName}>{operatorName || 'Military'}</span>
              </>
            );
          }
          return <span className="airline-text" title={operatorName}>{operatorName || 'Military'}</span>;
        }

        // For civilian aircraft, show airline logo
        // Try to get airline code from airline_icao field first
        let airlineCode = row.airline_icao;
        const airlineName = row.airline_name; // Get airline name for tooltip

        // If not available, try the callsign — not when it is the registration
        if (!airlineCode && row.flight) {
          const extractedCode = airlineCodeFromCallsign(row.flight, { registration: row.r });
          if (extractedCode && hasAirlineLogo(extractedCode)) {
            airlineCode = extractedCode;
          }
        }

        if (!airlineCode || !hasAirlineLogo(airlineCode)) {
          return <span className="airline-text" title={airlineName}>{airlineCode || '-'}</span>;
        }
        try {
          const logoPath = getAirlineLogo(airlineCode, 'icon');
          return (
            <>
              <img
                src={logoPath}
                alt={airlineCode}
                className="airline-logo"
                title={airlineName || airlineCode}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'inline';
                  }
                }}
              />
              <span className="airline-text" style={{display: 'none'}} title={airlineName}>{airlineCode}</span>
            </>
          );
        } catch (err) {
          return <span className="airline-text" title={airlineName}>{airlineCode}</span>;
        }
      }
    },
    { key: 'flight', label: 'Callsign' },
    { key: 'flight_iata', label: 'Flight', render: (row) => row.flight_iata || '-' },
    {
      key: 'dest_airport',
      label: 'Dest',
      // dest_airport comes from the tracker's target-airport inference, while
      // the ETA comes from the landing-prediction model, which carries its own
      // eta_airport. The two are independent, so a flight can legitimately have
      // an ETA with no destination. When that happens show the ETA's airport,
      // dimmed, so the row explains itself instead of showing a bare "-".
      render: (row) => {
        if (row.dest_airport) return row.dest_airport;
        if (row.eta_airport) {
          return (
            <span style={{ opacity: 0.55, fontStyle: 'italic' }} title="Airport the ETA is predicted for (no confirmed destination)">
              {row.eta_airport}
            </span>
          );
        }
        return '-';
      }
    },
    {
      key: 'eta_minutes',
      label: 'ETA',
      isNumeric: true,
      render: (row) => {
        if (!row.eta_time) return '-';
        const mins = Math.round(row.eta_minutes || 0);
        // Punctuality against the airline's scheduled arrival, when we have one.
        // Early is good (green); late is amber up to 20 min, red beyond.
        const delta = arrivalDeltaMinutes(row.eta_time, row.sched_arrival);
        const deltaColor = delta == null
          ? undefined
          : delta <= 0
            ? 'var(--color-success, #4caf50)'
            : delta <= 20
              ? 'var(--color-warning, #ff9800)'
              : 'var(--color-danger, #f44336)';
        const deltaLabel = delta == null
          ? null
          : delta === 0 ? 'on time' : delta < 0 ? `${Math.abs(delta)} min early` : `${delta} min late`;

        return (
          <span title={`~${mins} min to arrival${deltaLabel ? ` · ${deltaLabel}` : ''}`}>
            <span style={{ fontWeight: 'bold' }}>
              {formatEtaClock(row.eta_time, row.eta_airport)}
            </span>
            {delta != null && (
              <>
                <br />
                <span style={{ color: deltaColor, fontWeight: 'bold', fontSize: '0.85em' }}>
                  {delta > 0 ? `+${delta}` : delta} min
                </span>
              </>
            )}
          </span>
        );
      }
    },
    {
      key: 'aircraft_type',
      label: 'Type',
      render: (row) => {
        const aircraftType = row.t;
        const aircraftDesc = row.desc; // Get aircraft description for tooltip
        const spritePos = getAircraftSpritePosition(aircraftType);
        const sizeMultiplier = getAircraftSizeMultiplier(aircraftType);

        // Base size for table is 20px (smaller than map's 32px)
        const baseSize = 20;
        const iconSize = Math.round(baseSize * sizeMultiplier);

        // Calculate background position
        const bgPosX = -(spritePos.col * iconSize);
        const bgPosY = -(spritePos.row * iconSize);

        const iconStyle = {
          display: 'inline-block',
          width: `${iconSize}px`,
          height: `${iconSize}px`,
          backgroundImage: `url(${spriteImage})`,
          backgroundSize: `${iconSize * 8}px auto`,
          backgroundPosition: `${bgPosX}px ${bgPosY}px`,
          backgroundRepeat: 'no-repeat',
          filter: 'brightness(0) saturate(100%) brightness(0.3)', // Dark grey
          verticalAlign: 'middle',
          transform: 'rotate(90deg)', // Rotate so nose points right (east)
          marginLeft: '6px'
        };

        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }} title={aircraftDesc || aircraftType}>
            <span>{aircraftType || '-'}</span>
            <span style={iconStyle}></span>
          </span>
        );
      }
    },
    { key: 'squawk', label: 'Squawk' },
    {
      key: 'alt_baro',
      label: `Alt (${altUnit})`,
      isNumeric: true,
      render: (row) => {
        if (!row.alt_baro) return <span>-</span>;

        // Convert altitude based on unit system (backend sends feet)
        const convertedAlt = Math.round(convertAltitude(row.alt_baro, unitSystem));
        const baroRate = row.baro_rate || 0; // feet per minute

        // Calculate arrow angle based on baro_rate
        // 3000+ ft/min = 90 degrees (12 o'clock for climb, 6 o'clock for descent)
        // Level flight = 0 degrees (3 o'clock)
        const maxRate = 3000;
        const clampedRate = Math.max(-maxRate, Math.min(maxRate, baroRate));
        // Calculate angle: positive rate (climbing) = negative rotation (up from 3 o'clock)
        // negative rate (descending) = positive rotation (down from 3 o'clock)
        const rotation = -(clampedRate / maxRate) * 90;

        // Determine tooltip text
        let tooltipText = 'Level flight';
        if (baroRate > 100) {
          tooltipText = `Climbing at ${Math.abs(baroRate).toFixed(0)} ft/min`;
        } else if (baroRate < -100) {
          tooltipText = `Descending at ${Math.abs(baroRate).toFixed(0)} ft/min`;
        }

        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
            <span>{convertedAlt.toLocaleString()}</span>
            <span
              style={{
                display: 'inline-block',
                transform: `rotate(${rotation}deg)`,
                fontSize: '14px',
                lineHeight: 1,
                minWidth: '14px'
              }}
              title={tooltipText}
            >
              →
            </span>
          </span>
        );
      }
    },
    {
      key: 'baro_rate',
      label: `V/S (${getVerticalSpeedUnit(unitSystem)})`,
      isNumeric: true,
      render: (row) => {
        const baroRate = row.baro_rate;
        if (baroRate === undefined || baroRate === null) return <span>-</span>;

        // Convert based on unit system
        const converted = convertVerticalSpeed(baroRate, unitSystem);
        // Format with + or - sign
        const sign = converted >= 0 ? '+' : '';
        // For metric (m/s), show 1 decimal; for fpm show whole number
        const formatted = unitSystem === 'metric'
          ? converted.toFixed(1)
          : Math.round(converted);
        return <span>{sign}{formatted}</span>;
      }
    },
    {
      key: 'gs',
      label: `Spd (${unitSystem === 'aeronautical' ? 'kts' : unitSystem === 'imperial' ? 'mph' : 'km/h'})`,
      isNumeric: true,
      format: (val) => {
        if (!val) return '-';
        // Backend sends knots
        if (unitSystem === 'aeronautical') return Math.round(val); // keep knots
        if (unitSystem === 'imperial') return Math.round(val * 1.15078); // knots to mph
        return Math.round(val * 1.852); // knots to km/h
      }
    },
    {
      key: 'r_dst',
      label: `Dist (${distUnit})`,
      isNumeric: true,
      format: (val) => {
        if (!val) return '-';
        // Backend sends km
        const converted = convertDistance(val, unitSystem);
        return converted.toFixed(1);
      }
    },
  {
    key: 'lon',
    label: 'Long',
    isNumeric: true,
    render: (row) => {
      if (row.lon == null) {
        return <span className="verifying-position">Verifying...</span>;
      }
      return row.lon.toFixed(4);
    }
  },
  {
    key: 'lat',
    label: 'Lat',
    isNumeric: true,
    render: (row) => {
      if (row.lat == null) {
        return <span className="verifying-position">Verifying...</span>;
      }
      return row.lat.toFixed(4);
    }
  },
  { key: 'messages', label: 'Msgs', isNumeric: true },
  { key: 'rssi', label: 'RSSI', isNumeric: true, format: (val) => val != null ? val.toFixed(1) : '-' },
  {
    key: 'last_seen_at',
    label: 'Last Seen',
    render: (row) => {
      const lastSeenAt = row.last_seen_at;

      if (isGroundVehicle(row)) {
        // Ground vehicle - show inline truck icon
        return (
          <span className="ground-vehicle-cell">
            <GroundVehicleIcon size={14} />
            <span style={{fontSize: '11px'}}>Vehicle</span>
          </span>
        );
      }

      if (!lastSeenAt) {
        // Never seen before - show green dot indicator
        return (
          <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
            <span style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#00ff00',
              boxShadow: '0 0 4px #00ff00'
            }}></span>
            <span>New!</span>
          </span>
        );
      }

      const date = new Date(lastSeenAt);
      const now = new Date();

      // Check if same day (today)
      const isToday = date.toDateString() === now.toDateString();

      if (isToday) {
        // Today: show time in 24-hour format (e.g., 12:23)
        return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      }

      // Check if within past 7 days
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      sevenDaysAgo.setHours(0, 0, 0, 0);

      if (date >= sevenDaysAgo) {
        // Past 7 days: show 3-letter day (e.g., Mon, Tue)
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      }

      // Older than 7 days: show short date (e.g., 13 Nov)
      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'short' });
      return `${day} ${month}`;
    }
  },
  {
    key: 'military',
    label: 'Mil',
    render: (row) => {
      const isMilitary = row.military === true || isMilitaryAircraft(row.category);
      return isMilitary ? 'Y' : 'N';
    }
  },
  {
    key: 'inactivityTime',
    label: 'Inactive',
    isNumeric: true,
    render: (row) => {
      const inactivity = row.inactivityTime || 0;
      // Show seconds with 's' suffix, color-coded by urgency
      let color = '#666'; // Default grey
      if (inactivity >= 45) {
        color = '#dc3545'; // Red - about to expire
      } else if (inactivity >= 30) {
        color = '#fd7e14'; // Orange - warning
      } else if (inactivity >= 15) {
        color = '#ffc107'; // Yellow - attention
      }
      return (
        <span style={{ color, fontWeight: inactivity >= 30 ? 'bold' : 'normal' }}>
          {inactivity}s
        </span>
      );
    }
  },
  // Additional data columns
  {
    key: 'airline_icao',
    label: 'Airline ICAO',
    render: (row) => row.airline_icao || '-'
  },
  {
    key: 'airline_name',
    label: 'Airline Name',
    render: (row) => <span title={row.airline_name}>{row.airline_name || '-'}</span>
  },
  {
    key: 'operator',
    label: 'Operator',
    render: (row) => <span title={row.operator}>{row.operator || '-'}</span>
  },
  {
    key: 'desc',
    label: 'Description',
    render: (row) => <span title={row.desc}>{row.desc || '-'}</span>
  },
  {
    key: 'category',
    label: 'Category',
    render: (row) => row.category || '-'
  },
  {
    key: 'true_heading',
    label: 'Heading',
    isNumeric: true,
    render: (row) => {
      const heading = row.true_heading ?? row.mag_heading ?? row.track;
      if (heading == null) return <span>-</span>;
      return <span>{Math.round(heading)}°</span>;
    }
  },
  {
    key: 'track',
    label: 'Track',
    isNumeric: true,
    render: (row) => {
      if (row.track == null) return <span>-</span>;
      return <span>{Math.round(row.track)}°</span>;
    }
  },
  {
    key: 'country',
    label: 'Country',
    render: (row) => {
      const countryName = row.country;
      const countryCode = resolveCountryCode(row, { registration: 'r' });
      if (!countryName && !countryCode) return <span>-</span>;
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {countryCode && <span className={`flag flag-${countryCode.toLowerCase()}`}></span>}
          <span>{countryName || countryCode?.toUpperCase() || '-'}</span>
        </span>
      );
    }
  },
  {
    key: 'age',
    label: 'Age',
    isNumeric: true,
    render: (row) => {
      const age = row.inactivityTime || 0;
      // Color coding: Green < 5s, Yellow 5-20s, Red > 20s
      let color;
      if (age < 5) {
        color = '#28a745'; // Green
      } else if (age <= 20) {
        color = '#ffc107'; // Yellow
      } else {
        color = '#dc3545'; // Red
      }
      return (
        <span style={{ color, fontWeight: age > 20 ? 'bold' : 'normal' }}>
          {age}s
        </span>
      );
    }
  }
];
};

const DataTable = React.memo(function DataTable({
  aircraftData = {}, updatesPerSecond = 0, selectedAircraftHex = null, onAircraftSelect,
  sortConfig: controlledSort = null, onSortChange = null
}) {
  useAirlineLogoMapVersion();
  // Sort is owned here unless the parent passes sortConfig/onSortChange (the
  // mobile live table, which is unmounted while the map is shown).
  const [internalSort, setInternalSort] = useState({ key: 'r_dst', direction: 'asc' });
  const sortConfig = controlledSort || internalSort;
  const setSortConfig = useCallback((update) => {
    const next = typeof update === 'function' ? update(controlledSort || internalSort) : update;
    if (onSortChange) onSortChange(next); else setInternalSort(next);
  }, [controlledSort, internalSort, onSortChange]);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  // Get unit system from context (single interval at app level)
  const unitsSettings = useUnitsSettings();
  const currentUnitSystem = unitsSettings.units;

  // Get column configuration from context
  const { columnConfig } = useColumnConfig();
  const liveViewConfig = columnConfig?.tables?.liveView;

  // Check for mobile layout
  useEffect(() => {
    const checkMobile = () => {
      setIsMobileLayout(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Toggle card expansion
  const handleToggleExpand = useCallback((hex) => {
    setExpandedCard(prev => prev === hex ? null : hex);
  }, []);

  // Generate columns based on unit system and column configuration
  const columns = useMemo(() => {
    const allColumns = getColumns(currentUnitSystem);
    if (liveViewConfig) {
      return filterAndOrderColumns(allColumns, liveViewConfig);
    }
    return allColumns;
  }, [currentUnitSystem, liveViewConfig]);

  const aircraft = useMemo(() => {
    // Filter out any invalid entries and ensure we have valid aircraft objects
    const data = Object.values(aircraftData).filter(
      (ac) => ac && typeof ac === 'object' && ac.hex
    );

    if (!sortConfig.key) {
      return data;
    }

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Compare values
      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else {
        comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [aircraftData, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIndicator = (key) => {
    const isActive = sortConfig.key === key;
    const icon = !isActive ? '↕' : (sortConfig.direction === 'asc' ? '↑' : '↓');
    return (
      <span className={`sort-indicator${isActive ? ' active' : ''}`}>{icon}</span>
    );
  };

  // Mobile card view
  if (isMobileLayout) {
    return (
      <div className="data-table-container mobile-card-view">
        <div className="mobile-sort-bar">
          <span className="sort-label">Sort by:</span>
          <select
            className="mobile-sort-select"
            value={sortConfig.key}
            onChange={(e) => setSortConfig({ key: e.target.value, direction: sortConfig.direction })}
          >
            <option value="r_dst">Distance</option>
            <option value="flight">Callsign</option>
            <option value="alt_baro">Altitude</option>
            <option value="gs">Speed</option>
            <option value="t">Type</option>
          </select>
          <button
            className="sort-direction-btn"
            onClick={() => setSortConfig(prev => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }))}
          >
            {sortConfig.direction === 'asc' ? '↑' : '↓'}
          </button>
          <span className="aircraft-count">{aircraft.length} aircraft</span>
        </div>
        <div className="cards-container">
          {aircraft.length === 0 ? (
            <div className="no-data-card">No aircraft data available</div>
          ) : (
            aircraft.map((ac) => (
              <AircraftCard
                key={ac.hex}
                aircraft={ac}
                isSelected={selectedAircraftHex === ac.hex}
                isExpanded={expandedCard === ac.hex}
                onSelect={onAircraftSelect}
                onToggleExpand={handleToggleExpand}
                unitSystem={currentUnitSystem}
              />
            ))
          )}
        </div>
      </div>
    );
  }

  // Desktop table view
  return (
    <div className="data-table-container">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                onClick={() => handleSort(column.key)}
                className={`sortable${column.isNumeric ? ' numeric-column' : ''}`}
              >
                {column.label}{getSortIndicator(column.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {aircraft.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="no-data">
                No data available
              </td>
            </tr>
          ) : (
            aircraft.map((row) => {
              const isNew = !row.last_seen_at; // New aircraft if no last_seen_at timestamp
              const isSelected = selectedAircraftHex === row.hex;
              const hasNoPosition = row.lat == null || row.lon == null; // Aircraft without position data
              const isMilitary = row.military === true || isMilitaryAircraft(row.category);
              const emergency = getEmergencySquawk(row);
              return (
                <tr
                  key={row.hex}
                  title={emergency ? `Squawking ${emergency.code} — ${emergency.description}` : undefined}
                  className={`${isNew ? 'new-aircraft' : ''} ${isSelected ? 'selected-aircraft' : ''} ${hasNoPosition ? 'no-position-aircraft' : ''} ${isMilitary ? 'military-aircraft' : ''} ${emergency ? 'emergency-aircraft' : ''}`}
                  onClick={() => onAircraftSelect && onAircraftSelect(isSelected ? null : row)}
                  style={{ cursor: onAircraftSelect ? 'pointer' : 'default' }}
                >
                  {columns.map((column) => {
                    let displayValue;
                    if (column.render) {
                      // Use render function for JSX content
                      displayValue = column.render(row);
                    } else {
                      // Use format function or raw value
                      const value = row[column.key];
                      displayValue = column.format ? column.format(value) : (value || '-');
                    }
                    return (
                      <td key={column.key} data-col={column.key} className={`${column.isNumeric ? 'numeric-column' : ''} ${column.className || ''}`}>
                        {displayValue}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
});

export default DataTable;